import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, UserPlus, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, apiError } from '../../../lib/api';
import { toast } from '../../../stores/notificationStore';

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="field" style={{ marginBottom: 0 }}>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 600,
        color: 'var(--text-3)', textTransform: 'uppercase',
        letterSpacing: '0.06em', marginBottom: 5,
      }}>
        {label}{required && <span style={{ color: 'var(--accent)', marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const INITIAL_FORM = {
  first_name: '', last_name: '', other_names: '',
  gender: '', date_of_birth: '', nin: '',
  class_id: '', stream_id: '',
  guardian_name: '', guardian_phone: '', guardian_relationship: 'parent',
};

export function EnrollStudentModal({ onClose, onSuccess }: Props) {
  const { t } = useTranslation(['academic', 'common']);
  const qc = useQueryClient();

  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState('');

  const { data: classes = [] } = useQuery({
    queryKey: ['classes'],
    queryFn: () => api.get('/academic/classes').then(r => r.data.data ?? []),
  });

  const selectedClass = (classes as any[]).find((c: any) => c.id === form.class_id);
  const streams: { id: string; name: string }[] = selectedClass?.streams ?? [];

  function set(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }));
    setError('');
  }

  const { mutate, isPending } = useMutation({
    mutationFn: (data: object) => api.post('/academic/students', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['students'] });
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] });
      toast('success', t('academic:students.enroll') + ' successful');
      onSuccess?.();
      onClose();
    },
    onError: (err) => setError(apiError(err)),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    mutate({
      first_name: form.first_name,
      last_name: form.last_name,
      other_names: form.other_names || undefined,
      gender: form.gender,
      date_of_birth: form.date_of_birth,
      nin: form.nin || undefined,
      class_id: form.class_id,
      stream_id: form.stream_id || undefined,
      guardian: {
        full_name: form.guardian_name,
        phone: form.guardian_phone,
        relationship: form.guardian_relationship,
      },
    });
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg" role="dialog" aria-modal="true">

        {/* Header */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 8,
              background: 'var(--accent-bg)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <UserPlus size={16} color="var(--accent)" />
            </div>
            <div>
              <h2>{t('academic:students.enroll')}</h2>
              <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>
                Fill in the student's details below
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 30, height: 30, borderRadius: 7,
              background: 'var(--surface2)', border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            <X size={15} color="var(--text-3)" />
          </button>
        </div>

        {/* Body */}
        <form id="enroll-form" onSubmit={handleSubmit}>
          <div className="modal-body">

            {/* Personal Info */}
            <div className="modal-section">
              <div className="modal-section-title">Personal Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
                <Field label={t('academic:students.firstName')} required>
                  <input
                    className="input"
                    type="text"
                    value={form.first_name}
                    onChange={e => set('first_name', e.target.value)}
                    placeholder="e.g. Aisha"
                    required
                  />
                </Field>
                <Field label={t('academic:students.lastName')} required>
                  <input
                    className="input"
                    type="text"
                    value={form.last_name}
                    onChange={e => set('last_name', e.target.value)}
                    placeholder="e.g. Nakato"
                    required
                  />
                </Field>
                <Field label={`Other names (${t('common:optional')})`}>
                  <input
                    className="input"
                    type="text"
                    value={form.other_names}
                    onChange={e => set('other_names', e.target.value)}
                    placeholder="Middle name"
                  />
                </Field>
                <Field label={t('academic:students.gender')} required>
                  <select
                    className="select"
                    value={form.gender}
                    onChange={e => set('gender', e.target.value)}
                    required
                  >
                    <option value="">— Select —</option>
                    <option value="M">{t('academic:students.male')}</option>
                    <option value="F">{t('academic:students.female')}</option>
                  </select>
                </Field>
                <Field label={t('academic:students.dateOfBirth')} required>
                  <input
                    className="input"
                    type="date"
                    value={form.date_of_birth}
                    onChange={e => set('date_of_birth', e.target.value)}
                    required
                  />
                </Field>
                <Field label={`NIN (${t('common:optional')})`}>
                  <input
                    className="input"
                    type="text"
                    value={form.nin}
                    onChange={e => set('nin', e.target.value)}
                    placeholder="CM198503210001"
                  />
                </Field>
              </div>
            </div>

            {/* Class Assignment */}
            <div className="modal-section">
              <div className="modal-section-title">Class Assignment</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
                <Field label={t('academic:students.class')} required>
                  <select
                    className="select"
                    value={form.class_id}
                    onChange={e => { set('class_id', e.target.value); set('stream_id', ''); }}
                    required
                  >
                    <option value="">— Select class —</option>
                    {(classes as any[]).map((c: any) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </Field>
                {streams.length > 0 && (
                  <Field label={t('academic:students.stream')}>
                    <select
                      className="select"
                      value={form.stream_id}
                      onChange={e => set('stream_id', e.target.value)}
                    >
                      <option value="">— Any stream —</option>
                      {streams.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>
            </div>

            {/* Guardian */}
            <div className="modal-section">
              <div className="modal-section-title">Guardian / Parent</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
                <Field label={`${t('academic:students.guardian')} name`} required>
                  <input
                    className="input"
                    type="text"
                    value={form.guardian_name}
                    onChange={e => set('guardian_name', e.target.value)}
                    placeholder="Full name"
                    required
                  />
                </Field>
                <Field label={`${t('common:phone')}`} required>
                  <input
                    className="input"
                    type="tel"
                    value={form.guardian_phone}
                    onChange={e => set('guardian_phone', e.target.value)}
                    placeholder="+256700000000"
                    required
                  />
                </Field>
                <Field label="Relationship">
                  <select
                    className="select"
                    value={form.guardian_relationship}
                    onChange={e => set('guardian_relationship', e.target.value)}
                  >
                    <option value="parent">Parent</option>
                    <option value="guardian">Guardian</option>
                    <option value="sibling">Sibling</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                marginTop: 4,
                padding: '10px 14px', borderRadius: 8,
                background: 'var(--red-bg)', border: '1px solid rgba(220,38,38,0.25)',
                fontSize: 13, color: 'var(--red)',
              }}>
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-ghost">
              {t('common:buttons.cancel')}
            </button>
            <button type="submit" disabled={isPending} className="btn btn-primary" style={{ minWidth: 120 }}>
              {isPending ? (
                <><Loader2 size={14} style={{ animation: 'spin 0.6s linear infinite' }} /> Enrolling…</>
              ) : (
                <><UserPlus size={14} /> {t('academic:students.enroll')}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
